import React, { useState } from 'react';
import Flashbar, {
  FlashbarProps,
} from '@amzn/awsui-components-console/flashbar';
import { ChangeControlSeverity } from 'src/generated/graphql';
import { formatDayMonthYearTimeUtc } from 'src/common/utils/dateFormatters';
import {
  ActiveChangeControlWindow,
  useChangeControlStatus,
} from './useChangeControlStatus';
import { STATUS_ICON_ARIA_LABEL_IDS } from './statusIconAriaLabel';
import { TranslateFn, useTranslation } from 'src/i18n/shim/useTranslation';

/**
 * Flashbar type per severity. BLOCKED is an error because submissions that edit
 * live strings are refused outright without an approved override; RESTRICTED is a
 * warning because they are still possible with one.
 */
const FLASH_TYPE: Record<
  ActiveChangeControlWindow['severity'],
  FlashbarProps.Type
> = {
  [ChangeControlSeverity.Blocked]: 'error',
  [ChangeControlSeverity.Restricted]: 'warning',
};

/**
 * Builds the banner's Flashbar item. Exported so the copy can be unit-tested
 * against a fixed window without rendering the query.
 *
 * Header-less for both severities (`header` is optional on MessageDefinition), to
 * keep the banner's visual weight down. Body copy is identical for BLOCKED and
 * RESTRICTED, so severity is carried by the Flashbar `type` and — for anyone not
 * seeing the colour — by `statusIconAriaLabel` below.
 */
export const toFlashbarItem = (
  t: TranslateFn,
  locale: string,
  window: ActiveChangeControlWindow,
  onDismiss: () => void,
): FlashbarProps.MessageDefinition => ({
  id: 'change-control-window',
  type: FLASH_TYPE[window.severity],
  // The only way a screen reader hears the severity — see STATUS_ICON_ARIA_LABEL_IDS.
  statusIconAriaLabel: t(STATUS_ICON_ARIA_LABEL_IDS[window.severity]),
  // Dismissible, so the banner can be cleared once read rather than occupying the
  // notification slot on every route for the whole window. Dismissal is deliberately
  // NOT persisted (see the component): it lives in component state, so the next page
  // load shows the freeze again and the notice cannot be permanently hidden.
  dismissible: true,
  onDismiss,
  // Accessible name for the close button — without it the control is announced only
  // as "button".
  dismissLabel: t('cc-banner-dismiss-aria'),
  // `reason` is the freeze name from the change-control calendar and is passed
  // through untranslated; the surrounding sentence is a single resource so each
  // locale controls its own word order around both placeholders.
  content: t('cc-banner-content', {
    reason: window.reason,
    until: formatDayMonthYearTimeUtc(locale, new Date(window.windowEnd)),
  }),
});

/**
 * App-wide banner shown while the current moment is inside a CCNS change-control
 * (peak-freeze) window. Renders nothing on a normal day, while the status is still
 * loading, or when it could not be read — the banner is informational and fails
 * open; the backend submit path is the authoritative gate.
 *
 * Mounted once in the app shell's notification slot so every route shows it
 * without asking again.
 *
 * Dismissal is held in component state, not persisted. The shell keeps this mounted
 * across in-app navigation, so a dismissal survives route changes but not a page
 * load — a user cannot permanently hide an active freeze, and the banner reappears
 * on the next load for as long as the window is open.
 */
const ChangeControlBanner: React.FC = () => {
  const { t, locale } = useTranslation();
  const activeWindow = useChangeControlStatus();
  const [dismissed, setDismissed] = useState(false);

  if (!activeWindow || dismissed) {
    return null;
  }

  return (
    <Flashbar
      items={[
        toFlashbarItem(t, locale, activeWindow, () => setDismissed(true)),
      ]}
    />
  );
};

export default ChangeControlBanner;
